import { useRef, useState } from 'react';
import { Icon } from '../../ui/Icon/Icon';
import { MEDIA_MAX_FILE_SIZE_BYTES } from '../../../data/types';
import './MediaUploadArea.css';

interface MediaUploadAreaProps {
  disabled?: boolean;
  remainingSlots: number;
  onFilesSelected: (files: File[]) => void;
  /** Files that were dropped/picked but rejected for being over the size cap. */
  onFilesRejected?: (files: File[]) => void;
}

const ACCENT = 'var(--accent, #D23A3B)';
const T3 = 'var(--t3, #9CA3B8)';

/** Drag/drop + click-to-pick upload control. Accepts image and video; no name field (per spec). */
export default function MediaUploadArea({ disabled, remainingSlots, onFilesSelected, onFilesRejected }: MediaUploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const blocked = disabled || remainingSlots <= 0;

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || blocked) return;
    const candidates = Array.from(fileList).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    const files = candidates.filter((f) => f.size <= MEDIA_MAX_FILE_SIZE_BYTES);
    const oversized = candidates.filter((f) => f.size > MEDIA_MAX_FILE_SIZE_BYTES);
    if (oversized.length) onFilesRejected?.(oversized);
    if (files.length) onFilesSelected(files.slice(0, remainingSlots));
  };

  const boxClasses = [
    'media-upload-area-box',
    dragOver ? 'is-drag-over' : '',
    blocked ? 'is-blocked' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      onClick={() => { if (!blocked) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); if (!blocked) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
      className={boxClasses}
    >
      <div className="media-upload-icon-box">
        <Icon name="upload_file" size={24} color={dragOver ? ACCENT : T3} />
      </div>
      <div>
        <p className="media-upload-title">
          {blocked ? 'Media library is full' : 'Click or drag images / videos to upload'}
        </p>
        <p className="media-upload-subtitle">
          {blocked ? 'Delete media to free up a slot' : `JPG, PNG, GIF, MP4, MOV and similar formats · max 50MB · ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} available`}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="media-upload-hidden-input"
        disabled={blocked}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
